/** Dev/E2E diagnostics surface.
 * - window.__BLUEBOB__: game state + commands for Playwright assertions (PRD AC).
 * - window.__THREE_GAME_DIAGNOSTICS__: renderer stats for the profiler skill. */
import type { HudState } from '../types'

export interface ThreeDiagnostics {
  ready: boolean
  fps: number
  fpsAvg: number
  fpsMin: number
  fpsLow5: number
  calls: number
  triangles: number
  programs: number
  geometries: number
  textures: number
  /** Effective device-pixel-ratio the renderer is drawing at + the resulting backbuffer size.
   * Surfaced in the dev overlay so a DPR-driven fill-rate cap is visible at a glance. */
  dpr: number
  bufW: number
  bufH: number
  /** Adaptive 144 Hz governor state. Target is capped to the calibrated display refresh. */
  qualityTier: number
  renderScale: number
  adaptiveTargetFps: number
  displayRefreshHz: number
  detailFraction: number
  postEnabled: boolean
  shadowsEnabled: boolean
}

export interface BlueBobHarness {
  ready: boolean
  guiReady: boolean
  /** True once economy.init() has settled (server progress loaded + equipped reconciled). */
  econReady: boolean
  screen: string
  match: string
  inputDir: string
  hud: HudState
  player: { row: number; col: number; x: number; z: number; dir: string } | null
  jellies: Array<{ kind: string; row: number; col: number; mode: string; dist: number }>
  /** Cosmetics the BlueBob model is actually wearing (hatMeshes>0 means a hat is rendered). */
  cosmetics: { hat: string | null; skin: string | null; trail: string | null; hatMeshes: number }
  /** Hash of the current maze layout — changes when the level's arrangement changes. */
  mazeSig: number
  /** Current level theme name (e.g. "Coral Cave Trouble"). */
  themeName: string
  /** Active cutscene state (playing=false when none). */
  cutscene: { id: string; beat: number; beats: number; playing: boolean }
  // commands the harness can call to drive the game in tests
  setScreen?: (s: string) => void
  startGame?: () => void
  pause?: () => void
  resume?: () => void
  /** Halt/resume the render loop so the page goes static for screenshot capture. */
  freeze?: () => void
  unfreeze?: () => void
  /** Render one frame and return a JPEG data URL of the canvas (QA capture). */
  capture?: () => string
  /** Dev/E2E: locally unlock + equip a cosmetic so its rendering can be asserted. */
  equip?: (itemId: string) => void
  /** Dev/E2E: jump straight into a level (rebuilds its maze + theme) for screenshots/tests. */
  gotoLevel?: (n: number) => void
  /** Dev/E2E: play a cutscene by id ('intro' | 't2'..'t10' | 'ending') for screenshots/tests. */
  playCutscene?: (id: string) => void
  /** Dev/E2E: read live cutscene joint, bounds, and seabed diagnostics. */
  cutsceneStage?: { animationSnapshot: () => unknown; collisionSnapshot?: () => unknown; collisionCorrections?: () => string[]; collisionIssues?: () => unknown }
}

declare global {
  interface Window {
    __BLUEBOB__: BlueBobHarness
    __THREE_GAME_DIAGNOSTICS__: ThreeDiagnostics
  }
}

const harness: BlueBobHarness = {
  ready: false,
  guiReady: false,
  econReady: false,
  screen: 'boot',
  match: 'ready',
  inputDir: 'none',
  hud: {
    score: 0,
    hiScore: 0,
    lives: 3,
    level: 1,
    jelly: 0,
    pearlsRemaining: 0,
    powered: false,
    powerRatio: 0,
  },
  player: null,
  jellies: [],
  cosmetics: { hat: null, skin: null, trail: null, hatMeshes: 0 },
  mazeSig: 0,
  themeName: '',
  cutscene: { id: '', beat: 0, beats: 0, playing: false },
}

const threeDiag: ThreeDiagnostics = {
  ready: false,
  fps: 0,
  fpsAvg: 0,
  fpsMin: 0,
  fpsLow5: 0,
  calls: 0,
  triangles: 0,
  programs: 0,
  geometries: 0,
  textures: 0,
  dpr: 0,
  bufW: 0,
  bufH: 0,
  qualityTier: 4,
  renderScale: 1,
  adaptiveTargetFps: 144,
  displayRefreshHz: 144,
  detailFraction: 1,
  postEnabled: true,
  shadowsEnabled: true,
}

export function installDiagnostics(): BlueBobHarness {
  if (typeof window !== 'undefined') {
    window.__BLUEBOB__ = harness
    window.__THREE_GAME_DIAGNOSTICS__ = threeDiag
  }
  return harness
}

export function diag(): BlueBobHarness {
  return harness
}

/** Read-only accessor to the renderer diagnostics singleton (dev overlay reads dpr / buffer size). */
export function threeDiagnostics(): ThreeDiagnostics {
  return threeDiag
}

export function updateThreeDiagnostics(patch: Partial<ThreeDiagnostics>): void {
  Object.assign(threeDiag, patch)
}
